// Assistente IA (BETA, pedido 2026-10-20) — aba "Assistente" da barra de
// baixo do app (desde 2026-10-21; antes era tela cheia no Perfil). Abas: Chat (conversa com o agente), Agenda, Finanças e Saúde (personal, 2026-10-25)
// PESSOAIS do cliente (o que o agente anotou; nada a ver com a Agenda e o
// Financeiro da empresa). Só aparece pra quem /ai-assistant/me libera.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  HeartPulse,
  History,
  Loader2,
  MessageCircle,
  Plus,
  Settings,
  Trash2,
  Wallet,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { gpsDriveAiAssistantService, type AiMe } from "@/services/gpsdrive-ai-assistant.service";
import { AI_STYLES, AssistantAvatar, AssistantChat } from "./assistant-chat";
import { aiPalette } from "./ai-palette";
import { AiNotificationSettingsScreen } from "./ai-notification-settings";
import { SmartAgenda } from "./smart-agenda";
import { SmartFinance } from "./smart-finance";
import { SmartHealth } from "./smart-health";

type Tab = "chat" | "agenda" | "gastos" | "saude";

export function useAiAssistantAccess(enabled = true) {
  return useQuery({
    queryKey: ["ai-assistant", "me"],
    queryFn: () => gpsDriveAiAssistantService.me(),
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

const CONVERSATION_STORAGE = "gd_ai_conversation";

function readStoredConversation(): string | null {
  try {
    return localStorage.getItem(CONVERSATION_STORAGE);
  } catch {
    return null;
  }
}

// Aba "Assistente" da barra de baixo (pedido 2026-10-21: aba própria ao lado
// de Eventos, sem tela cheia, voltando na mesma conversa). Fica montada
// quando o cliente troca de aba; a conversa aberta também é lembrada no
// aparelho pra quando o app reabrir.
export function MobileAiAssistantPanel({ me, keyboardInset = 0 }: { me: AiMe; keyboardInset?: number }) {
  const [tab, setTab] = useState<Tab>("chat");
  const [conversationId, setConversationIdState] = useState<string | null>(readStoredConversation);
  const [showHistory, setShowHistory] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const setConversationId = (id: string | null) => {
    setConversationIdState(id);
    try {
      if (id) localStorage.setItem(CONVERSATION_STORAGE, id);
      else localStorage.removeItem(CONVERSATION_STORAGE);
    } catch {
      // sem armazenamento: só não lembra ao reabrir
    }
  };

  return (
    <div className="relative flex h-full min-h-0 flex-col bg-slate-100 dark:bg-slate-950" style={aiPalette(me.themeColor)}>
      <style>{AI_STYLES}</style>
      <header
        className="relative overflow-hidden ai-grad px-3 pb-3 shadow-[0_10px_30px_-12px_rgba(15,23,42,0.7)]"
        style={{ paddingTop: "calc(0.6rem + env(safe-area-inset-top))" }}
      >
        {/* brilhos decorativos */}
        <span className="pointer-events-none absolute -right-10 -top-14 h-40 w-40 rounded-full bg-white/20 blur-2xl" aria-hidden />
        <span className="pointer-events-none absolute -bottom-16 -left-10 h-40 w-40 rounded-full bg-white/15 blur-2xl" aria-hidden />
        <div className="relative flex items-center gap-3">
          <AssistantAvatar me={me} size={44} glow online />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-[17px] font-bold tracking-tight">{me.name}</span>
              <span className="rounded-full bg-white/20 px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-widest ring-1 ring-white/30">
                Beta
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs opacity-80">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-300" /> Online agora
            </div>
          </div>
          {tab === "chat" && (
            <>
              <HeaderButton label="Conversas anteriores" onClick={() => setShowHistory(true)}>
                <History className="h-[18px] w-[18px]" />
              </HeaderButton>
              <HeaderButton label="Nova conversa" onClick={() => setConversationId(null)}>
                <Plus className="h-[18px] w-[18px]" />
              </HeaderButton>
            </>
          )}
          <HeaderButton label="Configurar avisos" onClick={() => setShowSettings(true)}>
            <Settings className="h-[18px] w-[18px]" />
          </HeaderButton>
        </div>

        <nav className="relative mt-3 grid grid-cols-4 gap-1 rounded-2xl bg-white/15 p-1 ring-1 ring-white/20 backdrop-blur">
          {(
            [
              ["chat", MessageCircle, "Chat"],
              ["agenda", CalendarDays, "Agenda"],
              ["gastos", Wallet, "Finanças"],
              ["saude", HeartPulse, "Saúde"],
            ] as const
          ).map(([key, Icon, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={cn(
                "flex min-w-0 items-center justify-center gap-1 rounded-xl px-1 py-2 text-[13px] font-semibold transition-all",
                tab === key ? "bg-white ai-text shadow-md" : "opacity-85 hover:bg-white/10",
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{label}</span>
            </button>
          ))}
        </nav>
      </header>

      {/* Chat fica montado ao trocar pra Agenda/Gastos (não perde o que estava digitando). */}
      <div className={cn("flex min-h-0 flex-1 flex-col", tab !== "chat" && "hidden")}>
        <AssistantChat
          me={me}
          conversationId={conversationId}
          onConversationChange={setConversationId}
          bottomInset={keyboardInset}
          compactBottom
        />
      </div>
      {tab === "agenda" && <SmartAgenda me={me} keyboardInset={keyboardInset} />}
      {tab === "gastos" && <SmartFinance me={me} keyboardInset={keyboardInset} />}
      {tab === "saude" && <SmartHealth me={me} keyboardInset={keyboardInset} />}

      {showHistory && (
        <ConversationHistory
          current={conversationId}
          onClose={() => setShowHistory(false)}
          onOpen={(id) => {
            setConversationId(id);
            setShowHistory(false);
          }}
        />
      )}
      {showSettings && <AiNotificationSettingsScreen onClose={() => setShowSettings(false)} />}
    </div>
  );
}

function HeaderButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="grid h-9 w-9 place-items-center rounded-full bg-white/15 ring-1 ring-white/20 transition hover:bg-white/25 active:scale-95"
    >
      {children}
    </button>
  );
}

function ConversationHistory({
  current,
  onOpen,
  onClose,
}: {
  current: string | null;
  onOpen: (id: string | null) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ["ai-assistant", "conversations"],
    queryFn: () => gpsDriveAiAssistantService.conversations(),
  });
  const remove = useMutation({
    mutationFn: (id: string) => gpsDriveAiAssistantService.deleteConversation(id),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: ["ai-assistant", "conversations"] });
      if (id === current) onOpen(null);
    },
  });
  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-black/40" onClick={onClose}>
      <div
        className="mt-auto max-h-[75%] overflow-hidden rounded-t-3xl bg-white dark:bg-slate-900"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-center justify-between px-5 pb-2 pt-4">
          <h3 className="text-base font-semibold text-slate-900 dark:text-white">Conversas</h3>
          <button type="button" onClick={() => onOpen(null)} className="text-sm font-medium ai-text">
            + Nova
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-2 pb-4">
          {list.isLoading && <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-slate-400" />}
          {list.data?.length === 0 && <p className="py-8 text-center text-sm text-slate-500">Nenhuma conversa ainda.</p>}
          {list.data?.map((c) => (
            <div
              key={c.id}
              className={cn(
                "flex items-center gap-2 rounded-xl px-3 py-2.5",
                c.id === current && "ai-soft",
              )}
            >
              <button type="button" onClick={() => onOpen(c.id)} className="min-w-0 flex-1 text-left">
                <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">{c.title}</div>
                <div className="text-xs text-slate-500">
                  {new Date(c.updatedAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </div>
              </button>
              <button
                type="button"
                aria-label="Apagar conversa"
                onClick={() => remove.mutate(c.id)}
                className="grid h-9 w-9 place-items-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
